<template>
  <section :class="panelClass">
    <header :class="headerClass">
      <div v-if="!embedded" class="min-w-0">
        <div class="flex flex-wrap items-center gap-2">
          <h4 class="truncate text-sm font-semibold text-slate-900">{{ region.regionName || '未命名区域' }}</h4>
          <span class="status-badge bg-slate-100 text-slate-600">{{ customers.length }} 个客户</span>
        </div>
        <p class="mt-1 text-xs text-slate-500">客户 ID 使用当月名单客户 ID 选择</p>
      </div>
      <button
        type="button"
        class="btn-primary inline-flex items-center gap-1.5"
        :disabled="syncing"
        @click="emit('create-customer', region)"
      >
        <PlusIcon class="h-4 w-4" />
        新增客户
      </button>
    </header>

    <div class="data-table-scroll">
      <table class="data-table text-sm">
        <thead class="data-table-head">
          <tr>
            <th class="data-table-th is-center w-20">
              <div class="flex items-center justify-center gap-2">
                <span>选择</span>
                <label class="inline-flex cursor-pointer items-center justify-center">
                <input
                  type="checkbox"
                  class="form-check"
                  :checked="allCurrentSelected"
                  :disabled="syncing || customers.length === 0"
                  aria-label="选择当前区域全部客户"
                  @change="toggleCurrentRegion($event.target.checked)"
                >
                </label>
              </div>
            </th>
            <th class="data-table-th text-left">公司</th>
            <th class="data-table-th text-left">注册地</th>
            <th class="data-table-th is-right">目标额度</th>
            <th class="data-table-th text-left">允许范围</th>
            <th class="data-table-th is-right">利润率</th>
            <th class="data-table-th text-left">拆分提示</th>
            <th class="data-table-th is-center w-28">操作</th>
          </tr>
        </thead>
        <tbody class="data-table-body">
          <tr v-if="customers.length === 0">
            <td colspan="8" class="data-table-empty is-compact">暂无客户</td>
          </tr>
          <tr
            v-for="customer in customers"
            v-else
            :key="customer.id"
            class="data-table-row"
          >
            <td class="data-table-td is-center">
              <input
                type="checkbox"
                class="form-check"
                :value="customer.id"
                :checked="isSelected(customer.id)"
                :disabled="syncing"
                :aria-label="'选择客户 ' + displayCompanyName(customer)"
                @change="toggleCustomer(customer.id, $event.target.checked)"
              >
            </td>
            <td class="data-table-td">
              <div class="data-table-cell-title">{{ displayCompanyName(customer) }}</div>
              <div class="data-table-cell-meta">名单客户 ID：{{ customer.id || '-' }}</div>
            </td>
            <td class="data-table-td text-slate-600">{{ customer.registeredRegion || '-' }}</td>
            <td class="data-table-td is-right font-medium text-slate-900">{{ formatWanAmount(customer.targetOutputAmount) }}</td>
            <td class="data-table-td text-slate-600">{{ amountRange(customer) }}</td>
            <td class="data-table-td is-right">{{ formatProfitRate(customer.targetProfitRate) }}</td>
            <td class="data-table-td">
              <span class="status-badge bg-blue-50 text-blue-700">{{ splitHint(customer) }}</span>
            </td>
            <td class="data-table-td is-center">
              <div class="flex items-center justify-center gap-1.5">
                <button
                  type="button"
                  class="table-action-button is-primary"
                  title="编辑客户" aria-label="编辑客户"
                  :disabled="syncing"
                  @click="emit('edit-customer', customer)"
                >
                  <PencilSquareIcon class="h-4 w-4" />
                </button>
                <button
                  type="button"
                  class="table-action-button is-danger"
                  title="删除客户" aria-label="删除客户"
                  :disabled="syncing"
                  @click="emit('delete-customer', customer)"
                >
                  <TrashIcon class="h-4 w-4" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { formatProfitRate, formatWanAmount } from '../utils'

const props = defineProps({
  region: { type: Object, required: true },
  customers: { type: Array, default: () => [] },
  selectedIds: { type: Array, default: () => [] },
  syncing: { type: Boolean, default: false },
  embedded: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:selectedIds',
  'create-customer',
  'edit-customer',
  'delete-customer'
])

const embedded = computed(() => props.embedded)
const panelClass = computed(() => props.embedded ? 'bg-white' : 'rounded-lg border border-slate-200 bg-white')
const headerClass = computed(() => [
  'flex flex-wrap items-center justify-between gap-3 px-4 py-3',
  props.embedded ? 'border-b border-slate-100' : 'border-b border-slate-200'
])
const currentCustomerIds = computed(() => props.customers.map(customer => customer.id).filter(id => id !== null && id !== undefined))
const allCurrentSelected = computed(() => currentCustomerIds.value.length > 0 && currentCustomerIds.value.every(id => isSelected(id)))

function isSelected(customerId) {
  return props.selectedIds.some(id => String(id) === String(customerId))
}

function toggleCustomer(customerId, checked) {
  const nextIds = props.selectedIds.filter(id => String(id) !== String(customerId))
  if (checked) {
    nextIds.push(customerId)
  }
  emit('update:selectedIds', nextIds)
}

function toggleCurrentRegion(checked) {
  const currentIdSet = new Set(currentCustomerIds.value.map(id => String(id)))
  const nextIds = props.selectedIds.filter(id => !currentIdSet.has(String(id)))
  if (checked) {
    nextIds.push(...currentCustomerIds.value)
  }
  emit('update:selectedIds', nextIds)
}

function displayCompanyName(customer) {
  return customer.companyName || customer.customerName || customer.name || '-'
}

function firstDefined(customer, keys) {
  for (const key of keys) {
    if (customer[key] !== null && customer[key] !== undefined && customer[key] !== '') {
      return customer[key]
    }
  }
  return null
}

function amountRange(customer) {
  const minAmount = firstDefined(customer, [
    'minTargetOutputAmount',
    'targetOutputAmountMin',
    'minAllowedAmount',
    'allowedMinAmount',
    'amountLowerLimit',
    'lowerAmountLimit'
  ])
  const maxAmount = firstDefined(customer, [
    'maxTargetOutputAmount',
    'targetOutputAmountMax',
    'maxAllowedAmount',
    'allowedMaxAmount',
    'amountUpperLimit',
    'upperAmountLimit'
  ])
  if (minAmount === null && maxAmount === null) return '-'
  if (minAmount !== null && maxAmount !== null) return formatWanAmount(minAmount) + ' 至 ' + formatWanAmount(maxAmount)
  if (minAmount !== null) return '不低于 ' + formatWanAmount(minAmount)
  return '不高于 ' + formatWanAmount(maxAmount)
}

function splitHint(customer) {
  const amount = Number(customer.targetOutputAmount || 0)
  if (amount < 500000) return '允许 1 笔'
  if (amount < 1000000) return '建议 2 笔'
  if (amount < 3000000) return '至少 2 笔'
  return '建议 3 笔以上'
}
</script>
